import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const InterviewScheduler = ({ conversation, onClose, onSubmit }) => {
  const [scheduleData, setScheduleData] = useState({
    date: '',
    time: '',
    duration: '60',
    timezone: Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || 'America/New_York',
    interviewType: 'video',
    notes: '',
    agenda: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Toronto', label: 'Toronto (ET)' },
    { value: 'America/Vancouver', label: 'Vancouver (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' }
  ];

  const interviewTypeOptions = [
    { value: 'video', label: 'Video Call', description: 'Platform-native meeting room' },
    { value: 'phone', label: 'Phone Call', description: 'Traditional phone interview' },
    { value: 'in-person', label: 'In-Person', description: 'Face-to-face meeting' }
  ];

  const handleSubmit = async () => {
    if (!scheduleData?.date || !scheduleData?.time) return;

    setIsSubmitting(true);
    await onSubmit(scheduleData);
    setIsSubmitting(false);
  };

  const handleChange = (field, value) => {
    setScheduleData(prev => ({ ...prev, [field]: value }));
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow?.setDate(tomorrow?.getDate() + 1);
    return tomorrow?.toISOString()?.split('T')?.[0];
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-[#E5E7EB] sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Icon name="Calendar" size={20} color="#06B6D4" />
              </div>
              <div>
                <h3 className="text-base md:text-lg font-semibold text-[#111827]">Schedule Interview</h3>
                <p className="text-xs md:text-sm text-[#6B7280]">{conversation?.title}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
              aria-label="Close scheduler"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Company Info */}
          <div className="bg-[#F6F7FB] rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0025cc] to-[#0025cc] flex items-center justify-center flex-shrink-0">
                <Icon name="Briefcase" size={24} color="#FFFFFF" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm md:text-base font-semibold text-[#111827] mb-1">
                  {conversation?.title}
                </h4>
                <p className="text-xs md:text-sm text-[#6B7280]">{conversation?.companyInfo}</p>
              </div>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Interview Date"
              value={scheduleData?.date}
              onChange={(e) => handleChange('date', e?.target?.value)}
              required
              min={getMinDate()}
            />

            <Input
              type="time"
              label="Interview Time"
              value={scheduleData?.time}
              onChange={(e) => handleChange('time', e?.target?.value)}
              required
            />
          </div>

          {/* Duration & Timezone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Duration"
              options={durationOptions}
              value={scheduleData?.duration}
              onChange={(value) => handleChange('duration', value)}
              required
            />

            <Select
              label="Timezone"
              options={timezoneOptions}
              value={scheduleData?.timezone}
              onChange={(value) => handleChange('timezone', value)}
              searchable
              required
            />
          </div>

          {/* Interview Type */}
          <Select
            label="Interview Type"
            options={interviewTypeOptions}
            value={scheduleData?.interviewType}
            onChange={(value) => handleChange('interviewType', value)}
            required
          />

          {/* Meeting Link Info */}
          {scheduleData?.interviewType === 'video' && (
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="Video" size={20} color="#3B82F6" className="flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Video Meeting Room</h4>
                  <p className="text-xs text-blue-800">
                    A secure platform-native meeting room will be automatically created and shared with both parties via email and calendar invitation.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agenda */}
          <div>
            <label className="block text-sm font-medium text-[#111827] mb-2">
              Interview Agenda (Optional)
            </label>
            <textarea
              value={scheduleData?.agenda}
              onChange={(e) => handleChange('agenda', e?.target?.value)}
              placeholder="Outline topics to discuss during the interview..."
              className="w-full px-4 py-3 border border-[#E5E7EB] rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#5B2DFF] focus:border-transparent"
              rows={4}
            />
          </div>

          {/* Notes */}
          <Input
            type="text"
            label="Additional Notes (Optional)"
            placeholder="Any special instructions or requirements..."
            value={scheduleData?.notes}
            onChange={(e) => handleChange('notes', e?.target?.value)}
            description="These notes will be shared with the employer"
          />

          {/* Confirmation Notice */}
          <div className="bg-emerald-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={20} color="#10B981" className="flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-sm font-semibold text-emerald-900 mb-2">Interview Confirmation</h4>
                <ul className="text-xs text-emerald-800 space-y-1">
                  <li>• Both parties will receive calendar invitations</li>
                  <li>• Email confirmations with meeting details</li>
                  <li>• Automatic reminders 24 hours and 1 hour before</li>
                  <li>• You can reschedule or cancel up to 24 hours before</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 md:p-6 border-t border-[#E5E7EB] flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-white">
          <Button
            variant="outline"
            size="default"
            onClick={onClose}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="default"
            iconName="Calendar"
            iconPosition="left"
            loading={isSubmitting}
            onClick={handleSubmit}
            disabled={!scheduleData?.date || !scheduleData?.time}
            className="flex-1"
          >
            Schedule Interview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InterviewScheduler;